import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '@tonic-one/react';
import { ColumnDef, DataGrid } from '@tonic-one/react-data-grid';
import { RiskAiDetection, RiskAiRiskType } from '../types';
import { dayjs } from '@Utils/time';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { useOrgStore } from '@Models/useOrgModel';
import { useQuarantineStore } from '@Quarantine/store';
import { OperationTabUrl } from '@Quarantine/utils/constants';
import useRiskTypeBasedSelector from '@Quarantine/store/useRiskTypeBasedSelector';
import { useInfiniteQuery } from '@tanstack/react-query';
import TagListOverflow from '../TagListOverflow';
import { useDetectionTableQueryKey } from './useDetectionTableQueryKey';
import TextEllipsis, {
  TextEllipsisProps,
} from '@Components/Common/Tooltip/TextEllipsis';
import { useDataGridInfiniteScroll } from '@Utils/useDataGridInfiniteScroll';
import { useNavigateWithHandler } from '@Utils/hooks';

const RiskAiDetectionTable = (props: { filterInitialized?: boolean }) => {
  const { filterInitialized } = props;
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  const { orgsMap } = useOrgStore();
  const navigate = useNavigateWithHandler();

  const {
    populateTagFilters,
    populateRiskNameFilters,
    populateDetectedByFilters,
    fetchDetectionsPage,
    filters,
    sortOrder,
    setSortOrder,
  } = useQuarantineStore((state) => state.riskAi);
  const { columnConfig, setColumnConfig } = useRiskTypeBasedSelector();
  const { riskTypeFilter, serviceFilter, searchFilter } = filters;
  const dataGridRef = useRef<HTMLDivElement>(null);

  const handleError = useCallback(() => {
    window.showNotification({
      titleMsg: t('common.apiException.internalError'),
      severity: 'error',
    });
  }, [t]);

  const queryKey = useDetectionTableQueryKey({ sortOrder });

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey,
      enabled: filterInitialized === undefined ? true : filterInitialized,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 min
      initialPageParam: {
        page: 1,
        size: 50,
        sort: sortOrder,
        riskType: riskTypeFilter.currentValue,
        serviceName: serviceFilter.currentValue,
      },
      getNextPageParam: (
        lastPage: { data: RiskAiDetection[] | null; total: number },
        __,
        lastPageParam
      ) => {
        if (lastPageParam.page * 50 >= lastPage.total) {
          return undefined;
        }
        return {
          ...lastPageParam,
          page: lastPageParam.page + 1,
        };
      },
      queryFn: async (params) => {
        try {
          const { data } = await fetchDetectionsPage(params.pageParam);
          return { data: data.detections, total: data.total };
        } catch (error) {
          handleError();
          return { data: [], total: 0 };
        }
      },
    });

  const tableData = useMemo(() => {
    return (data?.pages?.flatMap(({ data }) => data).filter(Boolean) ??
      []) as RiskAiDetection[];
  }, [data?.pages]);

  const filteredTableData = useMemo(() => {
    let filtered = tableData;

    if (searchFilter.currentValue.trim()) {
      const searchTerm = searchFilter.currentValue.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        return (
          item.msgFrom.toLowerCase().includes(searchTerm) ||
          item.msgSubject.toLowerCase().includes(searchTerm) ||
          item.mailbox.toLowerCase().includes(searchTerm)
        );
      });
    }

    return filtered;
  }, [tableData, searchFilter.currentValue]);

  const handleNavigateToDetails = useCallback(
    (row: RiskAiDetection) => {
      const url = `${OperationTabUrl.RiskAi}/${row.messageId}`;
      navigate(url);
    },
    [navigate]
  );

  const columns = useMemo(() => {
    const cellProps = { style: { height: 72 } };

    const Ellipsis = (
      props: { children: React.ReactNode } & TextEllipsisProps
    ) => {
      const { children, ...rest } = props;
      return (
        <TextEllipsis lines={2} popperProps={{ usePortal: true }} {...rest}>
          {children}
        </TextEllipsis>
      );
    };

    return [
      {
        id: 'timestamp',
        header: t('riskAi.table.threat.header.timestamp'),
        accessorKey: 'timestamp',
        width: 180,
        isSortable: true,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <LinkButton
            onClick={() => handleNavigateToDetails(row.value)}
            textAlign="left"
            width="100%"
          >
            <Ellipsis>
              {dayjs(row.value.timestamp)
                .utcOffset(offset)
                .format(t('common.momentDateFormat'))}
            </Ellipsis>
          </LinkButton>
        ),
      },
      {
        id: 'companyGuid',
        header: t('riskAi.table.threat.header.companyGuid'),
        accessorKey: 'companyGuid',
        width: 175,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>
            {orgsMap.get(row.value.companyGuid) ?? row.value.companyGuid}
          </Ellipsis>
        ),
      },
      {
        id: 'scanSource',
        header: t('riskAi.table.threat.header.scanSource'),
        accessorKey: 'scanSource',
        width: 115,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis contentProps={{ textTransform: 'capitalize' }}>
            {row.value.scanSource}
          </Ellipsis>
        ),
      },
      {
        id: 'msgSubject',
        header: t('riskAi.table.threat.header.subject'),
        accessorKey: 'msgSubject',
        width: 175,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>{row.value.msgSubject}</Ellipsis>
        ),
      },
      {
        id: 'msgFrom',
        header: t('riskAi.table.threat.header.sender'),
        accessorKey: 'msgFrom',
        width: 115,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>{row.value.msgFrom}</Ellipsis>
        ),
      },
      {
        id: 'mailbox',
        header: t('riskAi.table.threat.header.recipient'),
        accessorKey: 'mailbox',
        width: 115,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>{row.value.mailbox}</Ellipsis>
        ),
      },
      {
        id: 'threatType',
        header: t('riskAi.table.threat.header.analysisResult'),
        accessorKey: 'threatType',
        width: 150,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>{row.value.threatType}</Ellipsis>
        ),
      },
      {
        id:
          riskTypeFilter.currentValue === RiskAiRiskType.SecurityRisk
            ? 'securityRiskName'
            : 'anomalyName',
        header:
          riskTypeFilter.currentValue === RiskAiRiskType.SecurityRisk
            ? t('riskAi.table.threat.header.securityRiskName')
            : t('riskAi.table.threat.header.anomalyName'),
        accessorKey: 'securityRiskName',
        width: 170,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>{row.value.securityRiskName}</Ellipsis>
        ),
      },
      {
        id: 'matchedFilters',
        header: t('riskAi.table.threat.header.analysisTag'),
        accessorKey: 'matchedFilters',
        width: 360,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => {
          if (row.value.malwareDetectedBy === 'LLM') return '-';
          return <TagListOverflow tags={row.value.matchedFilters} lines={2} />;
        },
      },
      {
        id: 'detectedBy',
        header: t('riskAi.table.threat.header.detectedBy'),
        accessorKey: 'detectedBy',
        width: 180,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>{row.value.detectedBy || '-'}</Ellipsis>
        ),
      },
      {
        id: 'action',
        header: t('riskAi.table.threat.header.action'),
        accessorKey: 'action',
        width: 115,
        isSortable: false,
        cellProps,
        cell: ({ row }: { row: { value: RiskAiDetection } }) => (
          <Ellipsis>
            {t(`quarantine.action.${row.value.action}`, row.value.action)}
          </Ellipsis>
        ),
      },
    ] satisfies ColumnDef[];
  }, [
    offset,
    orgsMap,
    riskTypeFilter.currentValue,
    t,
    handleNavigateToDetails,
  ]);

  const columnVisibility = useMemo(() => {
    return columnConfig.hideIds.reduce(
      (acc, id) => {
        acc[id] = false;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [columnConfig.hideIds]);

  const sorting = useMemo(
    () => ({
      columnId: 'timestamp',
      direction: sortOrder as 'asc' | 'desc',
    }),
    [sortOrder]
  );

  const handleSortingChange = useCallback(
    (sorting: { columnId: string; direction: 'asc' | 'desc' }) => {
      setSortOrder(sorting.direction ?? 'asc');
    },
    [setSortOrder]
  );

  const handleColumnReorder = useCallback(
    (order: string[]) => {
      setColumnConfig({ ...columnConfig, order });
    },
    [columnConfig, setColumnConfig]
  );

  useEffect(() => {
    if (data) {
      populateTagFilters(tableData);
      populateRiskNameFilters(
        tableData,
        [RiskAiRiskType.Anomaly, RiskAiRiskType.CustomizedAnomaly].includes(
          riskTypeFilter.currentValue
        )
          ? 'anomalyNameFilter'
          : 'securityRiskNameFilter'
      );
      populateDetectedByFilters(tableData);
    }
  }, [
    data,
    populateRiskNameFilters,
    populateTagFilters,
    populateDetectedByFilters,
    riskTypeFilter.currentValue,
    tableData,
  ]);

  useDataGridInfiniteScroll({
    dataGridRef,
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <DataGrid
      ref={dataGridRef}
      data={filteredTableData}
      columns={columns}
      isColumnSortable={true}
      autoSorting={false}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      isColumnResizable={true}
      isColumnReorderable={true}
      columnOrder={columnConfig.order}
      onColumnOrderChange={handleColumnReorder}
      columnVisibility={columnVisibility}
      showPagination={false}
      isLoading={!data || isFetchingNextPage}
      emptyContent={t('common.noData')}
    />
  );
};

export default RiskAiDetectionTable;
